import { DataColumn, DataColumns } from '@hb/react'
import {
  AnyObject,
  castDate,
  CollectionSort,
  getMonthString,
  ListDivider,
  ListDividerItem,
  ListDividerType,
  WithId,
} from '@hb/shared'

export const getFlexColumnWidth = <
  T extends AnyObject,
  ExtraProps extends AnyObject | undefined = AnyObject | undefined,
>({
  width,
  columns,
}: {
  width: number
  columns: DataColumns<T, ExtraProps>
}) => {
  const { widthColumns, flexColumns } = Object.values(columns).reduce<{
    widthColumns: Array<DataColumn<T, ExtraProps> & { width: number }>
    flexColumns: DataColumn<T, ExtraProps>[]
  }>(
    (acc, curr) => {
      if (curr.width) {
        acc.widthColumns.push(curr as DataColumn<any, any> & { width: number })
      } else {
        acc.flexColumns.push(curr)
      }
      return acc
    },
    { widthColumns: [], flexColumns: [] },
  )
  const widthColumnsWidth = widthColumns.reduce((acc, curr) => acc + curr.width, 0)

  // enforce a width of at least 100 for flex columns
  return Math.max(100, (width - widthColumnsWidth) / flexColumns.length)
}

// fill in missing month keys between two month keys (exclusive)
// eg: ['2021-01', '2021-03'] => ['2021-01', '2021-02', '2021-03']
const fillMissingMonthKeys = (monthKeys: Array<string>, sortDir: 'asc' | 'desc') => {
  if (!monthKeys.length) return []
  if (monthKeys.length === 1) return monthKeys
  const now = new Date()
  // limit year to 2 years from now
  const maxYear = now.getFullYear() + 2
  const first = sortDir === 'asc' ? monthKeys[0] : monthKeys[monthKeys.length - 1]
  const last = sortDir === 'asc' ? monthKeys[monthKeys.length - 1] : monthKeys[0]
  const [firstYear, firstMonth] = first.split('-').map(Number)
  const [parsedLastYear, parsedLastMonth] = last.split('-').map(Number)

  const lastYear = Math.min(parsedLastYear, maxYear)
  const lastMonth = parsedLastYear === lastYear ? parsedLastMonth : 12
  const months = []
  for (let year = firstYear; year <= lastYear; year += 1) {
    const startMonth = year === firstYear ? firstMonth + 1 : 1
    const endMonth = year === lastYear ? lastMonth : 12
    for (let month = startMonth; month <= endMonth; month += 1) {
      let m = month
      let y = year

      if (sortDir === 'desc') {
        if (m === 1) {
          m = 12
          y -= 1
        } else m -= 1
      }
      months.push(`${y}-${String(m).padStart(2, '0')}`)
    }
  }
  return sortDir === 'asc' ? months : months.toReversed()
}

const getMonthKeyLabel = (monthKey: string) => {
  const [year, month] = monthKey.split('-')
  return getMonthString(new Date(`${year}-${month}-01`))
}

const getDividerItems: Record<
  ListDividerType,
  (
    divider: ListDivider,
    items: WithId<any>[],
    sortDirection: 'asc' | 'desc',
  ) => Array<WithId<any> | ListDividerItem>
> = {
  month: (divider, items, sortDir) => {
    // ex: { '2021-01': 'January 2021' }
    const existingMonthKeys: Array<string> = []

    return items.reduce<Array<WithId<any> | ListDividerItem>>((acc, curr, i) => {
      if (i === 0) {
        const itemDate = curr[divider.propPath]
        if (!itemDate) return [{ _type: 'divider', label: 'No date' }, curr]
        const asDate = castDate(itemDate)
        const monthString = getMonthString(asDate)
        const monthKeyString = asDate.toISOString().slice(0, 7)
        existingMonthKeys.push(monthKeyString)
        return [{ _type: 'divider', label: monthString }, curr]
      }

      // if previous data item has a different month string, add dividers for missing months
      const prevItemDate = items[i - 1][divider.propPath]
      const currItemDate = curr[divider.propPath]
      const prevDate = prevItemDate ? castDate(prevItemDate) : null
      const currDate = currItemDate ? castDate(currItemDate) : null
      if (!currItemDate || !currDate || Number.isNaN(currDate)) return [...acc, curr]
      const currMonthKeyString = currDate.toISOString().slice(0, 7)
      const prevMonthKeyString = prevDate ? prevDate.toISOString().slice(0, 7) : null
      if (currMonthKeyString === prevMonthKeyString) return [...acc, curr]

      const monthKeys = fillMissingMonthKeys(
        existingMonthKeys.length
          ? [existingMonthKeys[existingMonthKeys.length - 1], currMonthKeyString]
          : [currMonthKeyString],
        sortDir,
      )
      existingMonthKeys.push(currMonthKeyString)
      return [
        ...acc,
        ...monthKeys.map(monthKey => ({
          _type: 'divider',
          label: getMonthKeyLabel(monthKey),
        })),
        curr,
      ]
    }, [])
  },
}

export const getItemsWithDividers = <T extends AnyObject = AnyObject>(
  items: WithId<T>[],
  columns: DataColumns<T>,
  sort: CollectionSort<T>,
): Array<WithId<T> | ListDividerItem> => {
  const selectedColumn = Object.entries(columns).find(([, c]) => c.sortKey === sort.field)
  if (!selectedColumn) return items
  const [, column] = selectedColumn
  const { dividers } = column
  if (!dividers) return items
  return dividers.reduce<Array<WithId<T> | ListDividerItem>>(
    (acc, divider) => getDividerItems[divider.type](divider, acc, sort.order),
    items,
  )
}
